export default {
  common: {
    blocking: 'Blocking',
    quality: 'Quality',
    manifold: 'Manifold',
    watertight: 'Watertight',
    meshReady: 'Mesh-ready',
    notMeshReady: 'Not mesh-ready',
    needsRepair: 'Needs repair',
    diagnosisFailed: 'Diagnosis failed',
    diagnosing: 'Diagnosing…',
    dismiss: 'Dismiss',
    backToOriginal: 'Back to original',
    geometry: 'geometry',
    blockingIssues_one: '{{count, number}} blocking issue',
    blockingIssues_other: '{{count, number}} blocking issues',
    qualityIssues: '{{count, number}} quality',
    advisoryCount: '{{count, number}} advisory',
    issuesSuffix_one: '{{count, number}} issue',
    issuesSuffix_other: '{{count, number}} issues',
  },
  verdictCard: {
    clean: 'Geometry is clean',
    cleanSubtitle: 'No topology or quality issues found',
    meshReady: 'Mesh-ready',
    meshReadySubtitle: 'Only advisory quality issues remain',
    needsRepair: 'Geometry needs repair',
    needsRepairSubtitle_one:
      '{{count, number}} blocking issue needs review before meshing',
    needsRepairSubtitle_other:
      '{{count, number}} blocking issues need review before meshing',
  },
  verdictLine: {
    diagnosisFailed: 'Diagnosis failed',
    diagnosing: 'Diagnosing…',
    wrap: 'Wrap — no diagnosis',
    noSeat: 'No Diagnosis seat',
    noSubscription: 'No Diagnosis subscription',
    clean: 'Clean',
    meshReady: 'Mesh-ready',
    advisory: '· {{count, number}} advisory',
    blockingIssues_one: 'blocking issue',
    blockingIssues_other: 'blocking issues',
    quality: '· {{count, number}} quality',
  },
  findingsRail: {
    fixInChat: 'Fix in chat',
    fixInChatHint:
      'Describe this finding to the assistant, which can edit the CAD to fix it.',
    reviewStep: 'Review Findings',
    repairStep: 'Repair',
    repairNotNeeded: 'No repairs needed',
    repairGuidance:
      'Auto-repair runs on upload when it can. For anything left, fix the part in your CAD tool and upload a new version from the library, or re-run diagnosis from the top bar.',
    beyondRepairTitle: 'Beyond automatic repair',
    beyondRepairFallback: 'Auto-repair could not fix the flagged issues.',
    beyondRepairBody:
      'Re-running won’t help — defects like pinch points and self-intersections have no automatic fix (healing collapses the solid; Fuse needs a shared face, not a single touch-point). Fix it in CAD: union the bodies into one solid so they share a face, or design out the pinch. A previous version may still be meshable — select it above, then open it in MeshGen (cell quality can degrade near the defects).',
    loading: '{{verb}} {{version}}…',
    simplifyRejectedTitle: 'Simplify rejected',
    simplifyRejectedBody:
      'The proposed CAD simplification was discarded — the previous version is unchanged. Select it above to view its diagnosis.',
    diagnosisFailedTitle: 'Diagnosis failed',
    diagnosisFailedFallback:
      "Geometry could not be analyzed. Try re-exporting from your CAD tool with 'heal geometry' enabled.",
    noDiagnosis: 'No diagnosis available for this geometry.',
    wrapTitle: 'No diagnosis for Wrap imports',
    wrapBody:
      'Wrap prep seals the part into one clean, watertight surface, so there’s nothing to diagnose — it’s ready to mesh.',
    wrapPrepNote:
      'Prep is set at import. To diagnose instead, re-import the file with Repair.',
    latestIssuesLabel: 'Issues on the latest diagnosed version',
    blockingTitle: 'Blocking — fix for a clean mesh',
    blockingNote:
      'Topology errors. Meshing can still run, but may fail or produce degraded cells — repairing the CAD is recommended.',
    qualityTitle: 'Quality — advisory',
    qualityNote: 'Won’t block meshing, but may degrade element quality.',
    showLayer: 'Show {{layer}} in viewer',
    hideLayer: 'Hide {{layer}} in viewer',
    expandBodies: 'Show which bodies overlap',
    collapseBodies: 'Hide which bodies overlap',
    recommended: 'Recommended',
    conformalFuseTooltip:
      'This is a conformal multi-region assembly — fusing merges the regions into one body (breaks CHT), so you’ll be asked to confirm first.',
    fuseTooltip:
      'Fuse redundant, overlapping solids into one watertight body — the usual fix when separate parts intersect. Creates a new CAD version you review before keeping it.',
    simplifyCad: 'Simplify CAD (fuse solids)',
    keepSeparateTooltip:
      'Keep the parts as separate bodies and mesh them with conformal (shared, matching) interfaces instead of fusing — use this when the distinct solids are intentional.',
    keepSeparateLink: 'or keep separate bodies (conformal interfaces)',
    doneIn: '{{verb}} in',
    allChecksPassed:
      'All diagnostic checks passed. You can open this geometry in MeshGen, or re-run diagnosis after replacing the file.',
    repairAlreadyAttempted: 'Repair already attempted',
    repairWithheld: 'Automatic repair withheld',
    alreadyTriedBody:
      'Auto-repair already ran on this part and didn’t resolve it, so it won’t keep retrying. Open it in MeshGen as-is (if mesh-ready), simplify the CAD below, or upload a corrected file.',
    ineligibleOriginBody:
      'This version came from a simplify or repair you chose — auto-repair won’t run on top of it. Review the findings and open it in MeshGen, or try another simplify.',
  },
  readinessChips: {
    combinedTooltip:
      'These show what this exact geometry is ready to mesh, per simulation type. A part can be ready for one and not another — e.g. an assembly with internal walls is fine for FEA but not external CFD.',
    staleTooltip:
      'This geometry was diagnosed before the mesh-readiness check was added. Re-run diagnosis to see what it’s ready to mesh, per simulation type.',
    feaReady:
      'Ready for FEA (structural / thermal / modal). This is a valid solid — it can be meshed as a volume. Multi-body assemblies mesh as separate per-body groups (Volume_1, Volume_2, …) for per-body material / BC assignment — no need to fuse them; shared internal walls are fine too.',
    extReady:
      'Ready for external-flow CFD. Clean closed body that can be wrapped in a wind tunnel and meshed. Non-overlapping multi-body assemblies are OK.',
    intReady:
      'Ready for internal-flow CFD. A fully enclosed internal cavity (the fluid volume) was extracted from this part.',
    // `{{reason}}` is one of the three readinessReason.internal* sentences —
    // `internalReason` picks the one matching the gate that actually ran.
    // A NEUTRAL frame: the reason carries the verdict, and the chip's own icon
    // and tint already say how severe it is. It led with "Not ready" until
    // ENG-1815, which reads as a contradiction on the amber tier — where the
    // reason is "openings found, run Extract", i.e. one click away rather than
    // blocked.
    intNotReady: 'Internal-flow CFD: {{reason}}.',
    chtReady:
      'Ready for conjugate heat transfer (CHT). The solid + fluid regions share a conformal interface — each meshes as its own region with a matching shared boundary (no fuse needed). The coincident interface faces / pinch points are expected, not defects.',
    chtWarn:
      'Meshable for CHT (conformal solid + fluid). Interface self-intersections / shared-wall artifacts may degrade cells near the seam, but the regions mesh.',
    feaNotReadyNotReady:
      'Not clean for FEA: {{reason}}. A self-intersecting solid meshes to invalid elements — repair before solving.',
    feaNotReadyWarn:
      'Not clean for FEA: {{reason}}. Repair improves element quality.',
    extNotReady:
      'Not clean for external-flow CFD: {{reason}}. A mesh can still generate (the wrapper is tolerant), but the cut may be unclean and results degraded.',
    chtNotReady: 'Not ready for CHT: {{reason}}.',
    feaLabel: 'FEA',
    externalLabel: 'External CFD',
    internalLabel: 'Internal CFD',
    chtLabel: 'CHT',
    readyToMesh: 'Ready to mesh',
    aboutReadiness: 'About mesh readiness',
    rerunToCheck: 'Re-run diagnosis to check.',
  },
  inspectorStrip: {
    found: 'found',
    isolated: 'Isolated',
    isolate: 'Isolate',
    closeInspector: 'Close inspector',
    // ENG-2470: the body pairs under the volume-overlap row. "Vol N" is the
    // same label Body Assignment uses, so a sub-row names a body the reader
    // can already find elsewhere.
    overlapPair: 'Vol {{a}} ↔ Vol {{b}}',
    overlapRegions: '×{{count, number}}',
    overlapUnresolved: '{{count, number}} unidentified',
    overlapUnresolvedWhy:
      'This diagnosis did not resolve these overlaps to bodies — either it predates body identification, or two bodies were too alike to tell apart. Re-run diagnosis to try again.',
    // ENG-2474: what each overlap IS. A coincident is two faces modelled
    // against each other that mismatch at the file's precision; an
    // interference has material to remove. The depth is the lens's mean
    // thickness, in µm below a millimetre so a hair and a stud read apart.
    overlapCoincident: 'coincident',
    overlapInterference: 'interference',
    overlapDepthMm: '{{value}} mm',
    overlapDepthUm: '{{value}} µm',
    overlapSplit:
      '{{coincident, number}} coincident · {{interference, number}} interference',
    overlapAllCoincident: '{{count, number}} coincident',
    overlapAllInterference: '{{count, number}} interference',
    // ENG-2475: what to DO about each pair. Cut removes the lens from one
    // body (the loser); Fragment makes the interface one conformal face;
    // Fuse welds the two into one body; Exclude changes nothing and records
    // the pair as intended independent. The op names double as the approval
    // panel's outcome labels.
    overlapSelectPair: 'Select Vol {{a}} ↔ Vol {{b}}',
    overlapOpFor: 'Treatment for Vol {{a}} ↔ Vol {{b}}',
    overlapOpChoose: 'Choose…',
    overlapOpCut: 'Cut',
    overlapOpFragment: 'Fragment',
    overlapOpFuse: 'Fuse',
    overlapOpExclude: 'Exclude',
    // The cut's loser is a physics call — the larger body by default, so a
    // stud stays a stud and the plate gains a bore — shown, never silent.
    overlapCutLoser: 'cuts Vol {{loser}}',
    overlapCutLoserUnknown: 'cuts the larger body',
    overlapSwapLoser: 'Cut Vol {{loser}} instead',
    overlapTreatSelectedNone: 'Treat selected pairs',
    overlapTreatSelected_one: 'Treat {{count, number}} selected pair',
    overlapTreatSelected_other: 'Treat {{count, number}} selected pairs',
    overlapTreatNone: 'Select at least one pair to treat.',
    overlapTreatChooseOp:
      'Choose a treatment for every selected pair — this diagnosis did not measure them, so there is no default.',
    // ENG-2501: the same batch button, compact, in the inspector strip — the
    // one surface that is always on screen.
    overlapTreatCompact: 'Treat {{count, number}}',
    // ENG-2501: what treating a pair IS, and what each op does, for a reader
    // meeting the four for the first time. Short — these are tooltips.
    overlapTreatExplainer:
      'Each overlapping pair can be cut, fragmented, fused or excluded. Ticked pairs are treated together as a new version you approve or reject.',
    overlapOpCutMeaning:
      'Remove the overlapping material from one body so the two just touch.',
    overlapOpFragmentMeaning:
      'Split the overlap into its own body so the interface is shared (bonded, shared nodes).',
    overlapOpFuseMeaning: 'Merge the two bodies into one.',
    overlapOpExcludeMeaning:
      'Leave both as they are and record the pair as intended. The geometry doesn’t change, so the mesh handles the pair as if untreated.',
    overlapCutLoserWhy:
      'The body that loses material; the other keeps its shape.',
    // ENG-2855: what each kind of overlap becomes in a shared-node FEA mesh,
    // and what SimOps then shows — the whole chain on an info button beside
    // the split line, one kind on each pair's pin button. Mesh modes and chips
    // are quoted from compMesh.meshSettings and simopsFiles.meshChips; keep
    // them in step.
    overlapFeaExplainerLabel:
      'What coincident and interference mean in an FEA mesh',
    // Captions under the explainer's two drawings; the × marks the lens
    // corners, where the stress is singular.
    overlapDiagramCoincident:
      'The faces merge, and both bodies share the nodes.',
    overlapDiagramInterference:
      'The lens becomes its own body. × marks edge stress that isn’t real.',
    overlapFeaExplainer:
      'In a mesh set to Shared nodes — bonded:\n\nCoincident: the mesh usually merges the faces, so the two bodies share nodes and are bonded. The joint carries tension, shear and compression and never opens.\n\nInterference: the overlap becomes its own small body, bonded to both, and the load passes through it. The stress at its edges isn’t a real result. Cut it so the faces just touch.\n\nIn SimOps, the mesh counts bonded pairs as Shared-face and faces it couldn’t merge as Unjoined pairs. Unjoined faces carry no load until you add a tie or contact pair. Stress at the edge of a bonded joint keeps rising as you refine the mesh, so read it a few elements away from the edge. A mesh set to Independent bodies bonds nothing: every joint needs a tie or contact pair, and an interference still needs cutting first.',
    overlapCoincidentFea:
      'Two faces drawn against each other that don’t quite match at the file’s precision. A shared-node mesh usually merges them into one bonded interface; Fragment does the same in the CAD.',
    overlapInterferenceFea:
      'Material that both bodies occupy. A shared-node mesh makes it its own small body, bonded to both, so the load funnels through it and the stress at its edges isn’t a real result. Cut removes it from one body (the larger, unless you swap), so the two just touch and mesh as one bonded face.',
  },
  approvalPanel: {
    fusing: 'Fusing redundant solids{{arrow}}…',
    treating_one: 'Treating {{count, number}} overlap pair{{arrow}}…',
    treating_other: 'Treating {{count, number}} overlap pairs{{arrow}}…',
    fixing: 'Fixing: {{defects}}',
    noSimplificationTitle: 'No simplification applied',
    noSimplificationFallback:
      'The CAD simplification did not improve the geometry.',
    backToOriginal: 'Back to original',
    eyebrowConformal: 'CAD simplification · conformal',
    eyebrowFuse: 'CAD simplification · fuse',
    eyebrowTreatOverlaps: 'CAD simplification · per-pair',
    reviewTitle: 'Review the simplified geometry',
    reviewDescription:
      'The CAD was edited to remove redundant internal interfaces.',
    reject: 'Reject',
    approve: 'Approve',
    before: 'Before',
    after: 'After',
    afterPending:
      'After counts aren’t available yet — the fused geometry is fully diagnosed when you approve it.',
    fusedIn: 'Fused in',
    treatedIn: 'Treated in',
    // ENG-2475: one line per pair the batch touched, and the total. A pair
    // that was skipped or failed carries the backend's reason verbatim.
    outcomesTitle: 'Per-pair outcomes',
    outcomeApplied: 'applied',
    outcomeRecorded: 'recorded',
    outcomeSkipped: 'skipped: {{detail}}',
    outcomeFailed: 'failed: {{detail}}',
    treatedTotal: '{{treated, number}} of {{total, number}} treated',
    // ENG-2501: hovering an outcome paints its pair on the viewer — but only
    // on the ORIGINAL. The outcomes name source body indices, and a fragment
    // renumbers most of the candidate's, so the treated version cannot be
    // painted until its own diagnosis names its bodies.
    outcomesHighlightOnSource:
      'Highlights show on the original until the treated version is re-diagnosed.',
    // ENG-2501: the loading line's sub-line for a per-pair batch — the pairs
    // and their ops, not the defect totals a fuse fixes.
    treatingPair: '{{pair}} · {{op}}',
    metric: 'Metric',
    beforeCol: 'Before',
    beforeColLabeled: 'Before · {{label}}',
    afterCol: 'After',
    afterColLabeled: 'After · {{label}}',
    solids: 'Solids',
    pinchPoints: 'Pinch points',
    nonManifoldEdges: 'Non-manifold edges',
    badWinding: 'Bad winding',
    manifold: 'Manifold',
    watertight: 'Watertight',
    yes: 'Yes',
    no: 'No',
  },
  noOpBanner: {
    fallback: 'The CAD simplification did not change the geometry.',
    noSimplificationApplied: 'No simplification applied.',
    backToOriginal: 'Back to original',
    dismiss: 'Dismiss',
  },
  report: {
    reportEyebrow: 'Diagnosis Report',
    geometry: 'Geometry',
    generated: 'Generated',
    meshReady: 'Mesh-ready',
    notMeshReady: 'Not mesh-ready',
    meshReadyDescription: 'No blocking topology issues were detected.',
    notMeshReadyDescription:
      'Blocking topology issues need review before meshing.',
    advisory: '{{count, number}} advisory',
    advisoryDescription: 'Quality issues do not automatically block meshing.',
    meshReadiness: 'Mesh readiness',
    manifold: 'Manifold',
    watertight: 'Watertight',
    open: 'Open',
    findings: 'Findings',
    blocking: 'Blocking',
    quality: 'Quality',
    noFindings: 'No findings detected.',
    versionHistory: 'Version history',
    yes: 'Yes',
    no: 'No',
  },
  reportDialog: {
    title: 'Diagnosis report',
    description: 'Review the report or save it from the browser print dialog.',
    printSave: 'Print / Save PDF',
  },
  conformalFuse: {
    title: 'Fuse a conformal assembly?',
    description:
      'This is a conformal multi-region assembly (e.g. a CHT solid + fluid). Fusing merges the regions into one body and breaks the multi-region setup — it meshes as-is for CHT, or use “keep separate bodies” for conformal interfaces. Only fuse if you intend a single solid (e.g. an FEA part modelled as separate halves).',
    keepSeparate: 'Keep separate',
    fuseAnyway: 'Fuse anyway',
  },
  topBar: {
    draftPill: 'v{{version}} · draft rev {{revision}}',
    exportSource: 'Export source file',
    studyChip: 'Study attached: {{study}}',
    productName: 'Diagnosis',
    backToLibrary: 'Back to library',
    meshReady: 'Mesh-ready',
    meshableTooltip:
      '{{summary}}. The geometry can still mesh, but cells/results may be degraded — repairing the CAD improves quality.',
    meshableTooltipNoSummary:
      'The geometry can still mesh, but some simulation types may produce degraded cells — repairing the CAD improves quality.',
    meshable: 'Meshable',
    notReadyTooltip:
      "{{summary}}At least one simulation type can't mesh this geometry — repair the CAD before meshing.",
    notMeshReady: 'Not mesh-ready',
    wrapImport: 'Wrap import',
    wrapRerunTooltip:
      'Wrap imports skip diagnosis. Re-import with Repair to diagnose.',
    diagnosisInProgress: 'Diagnosis in progress…',
    rerunTooltip: 'Re-run diagnosis',
    rerun: 'Re-run',
    exportPdf: 'Export PDF',
    exportOptions: 'Export options',
    exportJson: 'Export JSON',
    savePdfHint: 'Save the printable report as PDF from the browser dialog.',
    openInMeshgenDisabled: 'Geometry must be manifold and watertight to mesh',
    openInMeshgenReady: 'Open this geometry in MeshGen',
    openInMeshgenWarn:
      'Manifold & watertight — you can mesh, though some flagged issues remain',
    openInMeshgen: 'Open in MeshGen',
  },
  library: {
    dropTitle: 'Drop a CAD file to diagnose',
    dropSubtitle:
      'STEP, STP, OBJ, STL · up to 1 GB — checked for manifoldness, watertightness & mesh quality',
    recentGeometries: 'Recent geometries',
    countLabel: '{{count, number}} diagnosed',
    searchPlaceholder: 'Search geometries…',
    all: 'All',
    meshReady: 'Mesh-ready',
    needsRepair: 'Needs repair',
    emptyTitle: 'No diagnosed geometries yet',
    emptyTitleFiltered: 'No geometries match your filters',
    emptyHint: 'Upload a CAD file above to run your first diagnosis.',
    emptyHintSearch:
      'No matches in the loaded geometries — scroll to load more, or try a different search.',
    emptyHintFilter: 'Try a different filter or search term.',
    loadingLabel: 'Loading geometries…',
    // Row-level timestamp / status labels used by `diagnosedLabel` in
    // `useDiagnosisLibrary`. `diagnosedPrefix` is threaded to
    // `formatRelativeTime` as the "Diagnosed …" prefix, so the timestamp
    // reads "Diagnosed 3d ago" (EN) / "3日前に診断" / "3 天前診斷" naturally.
    diagnosedPrefix: 'Diagnosed',
    awaitingDiagnosis: 'Awaiting diagnosis',
    notDiagnosed: 'Not diagnosed',
  },
  libraryRow: {
    geometry: 'Geometry',
    diagnosed: 'Diagnosed',
    verdict: 'Verdict',
    open: 'Open',
  },
  libraryCard: {
    repaired: 'repaired',
  },
  thumbnail: {
    meshReady: 'Mesh-ready',
    needsRepair: 'Needs repair',
    failed: 'Failed',
    needsApproval: 'Needs approval',
    diagnosing: 'Diagnosing',
    wrap: 'Wrap',
    notDiagnosed: 'Not diagnosed',
  },
  viewerPanel: {
    loadingViewer: 'Loading viewer…',
  },
  fileTree: {
    files: 'Files ({{count, number}})',
    diagnosing: 'Diagnosing…',
    repairing: 'Repairing…',
    repairFailed: 'Repair failed',
    diagnosisFailed: 'Diagnosis failed',
    clean: 'Clean',
    doneWith: 'Done · {{summary}}',
    simplifyRejected: 'Simplify rejected',
    viewVersion: 'View version {{num}} ({{origin}})',
    downloadSourceFile: 'Download source file',
    downloadVersion: 'Download version {{num}} source file',
  },
  workspace: {
    lineageError: 'Could not load geometry versions.',
    fixInChatBusy: 'The assistant is replying. Wait for it to finish.',
    dismiss: 'Dismiss',
    rerunFailed: 'Re-run failed — try selecting the previous version.',
    simplifyFailed: 'Simplification could not be started.',
    resolveSimplifyFirst:
      'Resolve the simplification (approve or reject) before re-running diagnosis.',
    noGeometryToRediagnose:
      'This version has no geometry to re-diagnose — select the previous version.',
    simplifyAwaitingReview:
      'A simplification is already awaiting your review — approve or reject it first.',
    lineageBusy:
      'Another version is still diagnosing or repairing — wait for it to finish before simplifying.',
    simplifyInFlight: 'A simplification is already being submitted.',
    verbDiagnosing: 'Diagnosing',
    verbRepairing: 'Repairing',
    verbDiagnosed: 'Diagnosed',
    verbRepaired: 'Repaired',
  },
  // Compact defect pills shown on RepairHistory version rows — one per
  // diagnosis layer with count > 0. Kept short so the dense per-version
  // pill list stays scannable; the full names live under `layers.*` for
  // longer surfaces (verdict card, findings rail, inspector strip).
  tagAbbr: {
    non_manifold_edges: 'Non-manifold',
    non_manifold_vertices: 'NM vertices',
    boundary: 'Not watertight',
    winding_inconsistent_faces: 'Bad winding',
    self_intersections: 'Self-intersect',
    overlapping_faces: 'Overlapping',
    degenerate_faces: 'Degenerate',
    inter_volume_overlaps: 'Volume overlap',
    multi_revolution_surfaces: 'Multi-revolution',
    sharp_edges: 'Sharp edges',
    ultra_thin_surfaces: 'Thin surfaces',
    zero_length_edges: 'Zero-length',
    sliver_faces: 'Slivers',
    clean: 'Clean',
  },
  // Severity summary words (`diagnosisSeveritySummary`) — composed like
  // "3 critical · 12 blocking · 45 advisory" on the file-tree summary line
  // and CAD-simplify defect summary.
  severity: {
    clean: 'Geometry is clean',
    critical: 'critical',
    blocking: 'blocking',
    advisory: 'advisory',
    issuesDetected: 'Issues detected',
  },
  // Geometry meta abbreviations (diagnosisVerdict.ts::diagnosisMeta). Kept
  // as short abbreviations everywhere so the one-line stats row stays compact.
  meta: {
    vol: 'vol',
    surf: 'surf',
    curv: 'curv',
    diag: 'diag',
    sa: 'SA',
  },
  // Row labels for each DiagnosisLayer (mesh-viewer/diagnosisPalette). The
  // English string on the layer stays as the canonical technical term shown
  // in tooltips + printable reports; these are the translated display labels
  // rendered in the sidebar row and inspector strip.
  layers: {
    non_manifold_edges: { label: 'Non-manifold edges' },
    boundary: { label: 'Open boundaries' },
    winding_inconsistent_faces: { label: 'Inconsistent winding' },
    non_manifold_vertices: {
      label: 'Non-manifold vertices',
      hint: 'pinch point',
    },
    self_intersections: { label: 'Self-intersections' },
    overlapping_faces: { label: 'Overlapping faces' },
    degenerate_faces: { label: 'Degenerate faces' },
    inter_volume_overlaps: { label: 'Volume overlaps' },
    multi_revolution_surfaces: { label: 'Multi-revolution surfaces' },
    sharp_edges: { label: 'Sharp edges' },
    ultra_thin_surfaces: { label: 'Ultra-thin surfaces' },
    zero_length_edges: { label: 'Zero-length edges' },
    sliver_faces: { label: 'Sliver faces' },
  },
  defectDescriptions: {
    non_manifold_edges: {
      what: 'Edges shared by more than two faces — the mesher cannot resolve volume topology across them.',
      fix: 'Auto repair stitches or splits these edges; manual CAD cleanup is recommended above ~5k.',
    },
    boundary: {
      what: "Open edges where the surface doesn't close into a watertight solid.",
      fix: 'Auto repair caps small gaps; large openings need the missing faces added in CAD.',
    },
    winding_inconsistent_faces: {
      what: 'Adjacent faces whose normals point in opposite directions, breaking inside/outside classification.',
      fix: 'Auto repair re-orients windings from a seed face.',
    },
    non_manifold_vertices: {
      what: 'Pinch point (a non-manifold vertex) — two solids or surfaces meet at a single point, so the interior is ambiguous there.',
      fix: 'Join the bodies in CAD so they share a face instead of touching at a point. Auto-repair usually cannot resolve this.',
    },
    self_intersections: {
      what: 'Surfaces that pass through themselves or each other, producing tangled topology.',
      fix: "Defeature or repair the intersecting region in CAD — the mesher can't resolve it.",
    },
    overlapping_faces: {
      what: 'Coincident or overlapping faces occupying the same space.',
      fix: 'Remove the duplicate face or merge the bodies in CAD.',
    },
    degenerate_faces: {
      what: 'Faces collapsed to a line or point with effectively zero area.',
      fix: 'Re-export with a tighter tolerance or remove the sliver feature in CAD.',
    },
    inter_volume_overlaps: {
      what: 'Separate solid volumes that interpenetrate.',
      fix: 'Boolean-union or separate the bodies in CAD before meshing.',
    },
    multi_revolution_surfaces: {
      what: 'Surfaces revolved more than 360° that fold back on themselves.',
      fix: 'Trim the revolution to a single turn in CAD.',
    },
    sharp_edges: {
      what: "Feature edges sharper than the mesher's default dihedral threshold (30°).",
      fix: 'Advisory — preserved as feature lines unless suppressed.',
    },
    ultra_thin_surfaces: {
      what: 'Surface pairs closer than 0.01× the bounding-box diagonal; they produce degenerate cells.',
      fix: 'Consider defeaturing or local sizing in Mesh Settings.',
    },
    zero_length_edges: {
      what: 'Edges whose endpoints coincide — zero length in the tessellation.',
      fix: 'Advisory — re-tessellate on import with a tighter chord tolerance.',
    },
    sliver_faces: {
      what: 'Faces with an aspect ratio above 50:1 in the source tessellation.',
      fix: 'Advisory — re-tessellate on import with a tighter chord tolerance.',
    },
    fallback: {
      what: 'A geometry defect detected during diagnosis.',
      fix: 'Auto repair attempts a fix where possible; otherwise repair the geometry in CAD.',
    },
  },
} as const;
